<template>
  <div class="dynamic-page-content">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
    <div v-else-if="error" class="error-state">
      <h2>Page Not Found</h2>
      <p>{{ error }}</p>
      <router-link to="/" class="back-link">← Back to Home</router-link>
    </div>
    <div v-else class="page-body">
      <!-- Rich text body from CKEditor -->
      <div v-if="body" class="rich-text" v-html="body"></div>

      <!-- Sections -->
      <div v-for="section in sections" :key="section.id" class="page-section">
        <h2 v-if="section.title" class="section-title">{{ section.title }}</h2>
        <div v-if="section.body" class="rich-text" v-html="section.body"></div>
        <img v-if="section.image" :src="section.image" :alt="section.title" class="section-image" />
      </div>
    </div>
  </div>
</template>

<script>

import api from '@/services/api';
import { mediaUrl, resolveRichText } from '@/services/media';

export default {
  name: 'DynamicPageContent',
  props: {
    slug: { type: String, required: true }
  },
  data() {
    return {
      loading: true,
      error: null,
      body: '',
      sections: [],
      pageTitle: '',
      headerImage: ''
    }
  },
  watch: {
    slug: 'fetchPage'
  },
  async created() {
    await this.fetchPage();
  },
  methods: {
    async fetchPage() {
      this.loading = true;
      this.error = null;
      try {
        const page = await api.getPageBySlug(this.slug);
        if (page) {
          this.pageTitle = page.title || '';
          this.body = resolveRichText(page.body || '');
          // Backend provides header_image (alias for hero_image) and hero_image
          this.headerImage = page.header_image ? mediaUrl(page.header_image) : (page.hero_image ? mediaUrl(page.hero_image) : '');
          // Sections: body is alias for content
          this.sections = (page.sections || []).map(s => ({
            id: s.id,
            title: s.title,
            body: resolveRichText(s.body || s.content || ''),
            image: s.image ? mediaUrl(s.image) : null
          }));
          this.$emit('page-loaded', { title: this.pageTitle, image: this.headerImage });
        }
      } catch (e) {
        this.error = 'The page you are looking for does not exist or could not be loaded.';
        console.error('Failed to load page', e.message);
      }
      this.loading = false;
    }
  }
}

</script>

<style scoped>
.dynamic-page-content { max-width: 900px; margin: 0 auto; padding: 40px 20px; min-height: 40vh; }
.loading-state, .error-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; color: #666; }
.spinner { width: 40px; height: 40px; border: 4px solid #eee; border-top-color: #C02130; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.error-state h2 { color: #C02130; margin-bottom: 10px; }
.back-link { margin-top: 20px; color: #C02130; text-decoration: none; font-weight: 600; }
.back-link:hover { text-decoration: underline; }
.page-body { color: #333; line-height: 1.7; }
.rich-text { font-size: 16px; }
.rich-text :deep(h1), .rich-text :deep(h2), .rich-text :deep(h3) { color: #2c3e50; margin: 24px 0 12px; }
.rich-text :deep(img) { max-width: 100%; height: auto; border-radius: 12px; margin: 16px 0; }
.rich-text :deep(table) { width: 100%; border-collapse: collapse; margin: 16px 0; }
.rich-text :deep(th), .rich-text :deep(td) { padding: 10px; border: 1px solid #ddd; text-align: left; }
.rich-text :deep(th) { background: #C02130; color: white; }
.page-section { margin-top: 40px; }
.section-title { color: #C02130; font-size: 24px; font-weight: 700; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 3px solid #ff9800; }
.section-image { max-width: 100%; border-radius: 12px; margin-top: 16px; }
</style>
